/**
 * Texture Loader
 * 
 * Wrapper around Three.js TextureLoader with caching,
 * automatic color space handling, and PBR texture support.
 */

import * as THREE from 'three';
import type { IAssetLoader, PBRTextures } from '../types';

export interface TextureOptions {
  colorSpace?: THREE.ColorSpace;
  wrapS?: THREE.Wrapping;
  wrapT?: THREE.Wrapping;
  flipY?: boolean;
  anisotropy?: number;
  generateMipmaps?: boolean;
}

export interface LoadedPBRTextures {
  map?: THREE.Texture;
  normalMap?: THREE.Texture;
  roughnessMap?: THREE.Texture;
  metalnessMap?: THREE.Texture;
  aoMap?: THREE.Texture;
  emissiveMap?: THREE.Texture;
}

export class TextureLoader implements IAssetLoader<THREE.Texture> {
  private loader: THREE.TextureLoader;
  private cache = new Map<string, THREE.Texture>();
  private maxAnisotropy: number;
  
  constructor(maxAnisotropy = 4) {
    this.loader = new THREE.TextureLoader();
    this.maxAnisotropy = maxAnisotropy;
  }
  
  /**
   * Load a single texture
   */
  public async load(
    url: string,
    onProgress?: (progress: number) => void,
    options?: TextureOptions
  ): Promise<THREE.Texture> {
    // Check cache first
    const cacheKey = this.getCacheKey(url, options);
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }
    
    return new Promise((resolve, reject) => {
      this.loader.load(
        url,
        (texture) => {
          this.applyOptions(texture, options);
          this.cache.set(cacheKey, texture);
          resolve(texture);
        },
        (event) => {
          if (onProgress && event.lengthComputable) {
            onProgress(event.loaded / event.total);
          }
        },
        (error) => {
          reject(new Error(`Failed to load texture: ${url} - ${error}`));
        }
      );
    });
  }
  
  /**
   * Load a full set of PBR textures
   */
  public async loadPBRTextures(
    textures: PBRTextures,
    onProgress?: (progress: number) => void
  ): Promise<LoadedPBRTextures> {
    const result: LoadedPBRTextures = {};
    const urls = Object.entries(textures).filter(([, url]) => url);
    const total = urls.length;
    let loaded = 0;
    
    const loadPromises = urls.map(async ([key, url]) => {
      if (!url) return;
      
      // Determine correct color space based on texture type
      const isColorData = key === 'map' || key === 'emissiveMap';
      const options: TextureOptions = {
        colorSpace: isColorData ? THREE.SRGBColorSpace : THREE.LinearSRGBColorSpace,
        flipY: false, // glTF models expect flipY = false
      };
      
      try {
        const texture = await this.load(url, undefined, options);
        loaded++;
        onProgress?.(loaded / total);
        
        // Assign to result
        (result as Record<string, THREE.Texture>)[key] = texture;
      } catch (error) {
        console.warn(`Failed to load ${key} texture: ${url}`, error);
      }
    });
    
    await Promise.all(loadPromises);
    return result;
  }
  
  /**
   * Apply texture options
   */
  private applyOptions(texture: THREE.Texture, options?: TextureOptions): void {
    // Color space (sRGB for color, Linear for data textures)
    texture.colorSpace = options?.colorSpace ?? THREE.SRGBColorSpace;
    
    // Wrapping
    texture.wrapS = options?.wrapS ?? THREE.RepeatWrapping;
    texture.wrapT = options?.wrapT ?? THREE.RepeatWrapping;
    
    // Flip (usually false for glTF)
    texture.flipY = options?.flipY ?? true;
    
    // Anisotropic filtering for sharper textures at angles
    texture.anisotropy = options?.anisotropy ?? this.maxAnisotropy;
    
    // Mipmaps
    texture.generateMipmaps = options?.generateMipmaps ?? true;
    
    texture.needsUpdate = true;
  }
  
  /**
   * Generate cache key from URL and options
   */
  private getCacheKey(url: string, options?: TextureOptions): string {
    if (!options) return url;
    return `${url}|${options.colorSpace}|${options.flipY}`;
  }
  
  /**
   * Clear a specific texture from cache
   */
  public clearFromCache(url: string): void {
    for (const [key, texture] of this.cache) {
      if (key.startsWith(url)) {
        texture.dispose();
        this.cache.delete(key);
      }
    }
  }
  
  /**
   * Dispose all cached textures
   */
  public dispose(): void {
    for (const texture of this.cache.values()) {
      texture.dispose();
    }
    this.cache.clear();
  }
}
